import { useCallback, useEffect, useMemo, useState } from 'react'
import { pocketbaseErrorMessage } from '../lib/pocketbaseErrorMessage'
import { formatMachineLabel } from '../lib/machines'
import { loadCompatibleMachines } from '../lib/productMachines'
import {
  cancelSlotOperation,
  moveSlotSameMachine,
  refreshInProgressReservation,
  transferSlotToMachine,
} from '../lib/scheduleOperations'
import { formatLocalDateTimeInput, localDateTimeToIso } from '../lib/scheduleCalculations'
import type { MachineRecord, ProductRecord, ScheduleSlotRecord } from '../types/records'
import { Alert } from './ui'
import { FormField, formInputProps } from './ui/FormField'
import { modalBtn } from './ui/Modal'
import { btn, cn } from '../lib/ui'

export type ScheduleOperationPanelProps = {
  slot: ScheduleSlotRecord
  product?: ProductRecord | null
  machines: MachineRecord[]
  canWrite: boolean
  userId?: string
  onDone: (message?: string) => void
  onError: (msg: string) => void
}

function toLocalInput(iso: string): string {
  return formatLocalDateTimeInput(new Date(iso))
}

export function ScheduleOperationPanel({
  slot,
  product,
  machines,
  canWrite,
  userId,
  onDone,
  onError,
}: ScheduleOperationPanelProps) {
  const [tab, setTab] = useState<'move' | 'transfer' | 'stop'>('move')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  const [moveStart, setMoveStart] = useState(() => toLocalInput(slot.plannedStart))
  const [moveEnd, setMoveEnd] = useState(() => toLocalInput(slot.plannedEnd))

  const [targetMachineId, setTargetMachineId] = useState(slot.machine)
  const [transferStart, setTransferStart] = useState(() => toLocalInput(slot.plannedStart))
  const [compressSource, setCompressSource] = useState(true)

  const [compressOnCancel, setCompressOnCancel] = useState(slot.status === 'planned')

  const [compatibleMachines, setCompatibleMachines] = useState<MachineRecord[]>([])

  useEffect(() => {
    if (!product?.id) {
      setCompatibleMachines(machines.filter((m) => m.id === slot.machine))
      return
    }
    void loadCompatibleMachines(product.id).then(setCompatibleMachines)
  }, [product?.id, slot.machine])

  const machineOptions = useMemo(() => {
    if (compatibleMachines.length > 0) return compatibleMachines
    return machines.filter((m) => m.id === slot.machine)
  }, [compatibleMachines, machines, slot.machine])

  const inProgress = slot.status === 'in_progress'

  const confirmInProgress = useCallback((): boolean => {
    if (!inProgress) return true
    return window.confirm(
      'Слот в работе — материалы зарезервированы. Перенос пересчитает резерв. Продолжить?',
    )
  }, [inProgress])

  const handleMove = useCallback(async () => {
    if (!canWrite || !confirmInProgress()) return
    setBusy(true)
    setMsg('')
    onError('')
    try {
      const startIso = localDateTimeToIso(moveStart)
      const endIso = localDateTimeToIso(moveEnd)
      const { updated } = await moveSlotSameMachine(slot.id, startIso, endIso)
      if (inProgress && product?.id) {
        await refreshInProgressReservation(
          { ...slot, plannedStart: startIso, plannedEnd: endIso },
          product.id,
          userId,
        )
      }
      setMsg(`Перенос выполнен. Обновлено слотов: ${updated}.`)
      onDone(`Слот перенесён (${updated} сл. на станке)`)
    } catch (e: unknown) {
      onError(pocketbaseErrorMessage(e, 'Ошибка переноса'))
    } finally {
      setBusy(false)
    }
  }, [canWrite, confirmInProgress, moveStart, moveEnd, slot, inProgress, product?.id, userId, onDone, onError])

  const handleTransfer = useCallback(async () => {
    if (!canWrite || !confirmInProgress()) return
    if (!targetMachineId) {
      onError('Выберите целевой станок')
      return
    }
    const targetMachine = machines.find((m) => m.id === targetMachineId)
    if (!targetMachine) {
      onError('Станок не найден')
      return
    }
    setBusy(true)
    setMsg('')
    onError('')
    try {
      const startIso = localDateTimeToIso(transferStart)
      const { updated } = await transferSlotToMachine(slot.id, {
        targetMachineId,
        targetMachine,
        product,
        newStartIso: startIso,
        compressSourceQueue: compressSource && targetMachineId !== slot.machine,
        createdBy: userId,
      })
      if (inProgress && product?.id) {
        await refreshInProgressReservation({ ...slot, machine: targetMachineId }, product.id, userId)
      }
      setMsg(`Перенос на станок выполнен. Обновлено слотов: ${updated}.`)
      onDone(`Слот на другом станке (${updated} сл.)`)
    } catch (e: unknown) {
      onError(pocketbaseErrorMessage(e, 'Ошибка переноса на станок'))
    } finally {
      setBusy(false)
    }
  }, [
    canWrite,
    confirmInProgress,
    targetMachineId,
    machines,
    slot,
    product,
    transferStart,
    compressSource,
    userId,
    inProgress,
    onDone,
    onError,
  ])

  const handleCancel = useCallback(async () => {
    if (!canWrite) return
    if (inProgress && !window.confirm('Отменить слот в работе? Резерв материалов будет снят.')) return
    if (!window.confirm('Отменить этот слот?')) return
    setBusy(true)
    setMsg('')
    onError('')
    try {
      const { updated } = await cancelSlotOperation(slot.id, {
        compressQueue: compressOnCancel,
        createdBy: userId,
      })
      setMsg(`Слот отменён. Обновлено слотов: ${updated}.`)
      onDone('Слот отменён')
    } catch (e: unknown) {
      onError(pocketbaseErrorMessage(e, 'Ошибка отмены'))
    } finally {
      setBusy(false)
    }
  }, [canWrite, inProgress, compressOnCancel, slot.id, userId, onDone, onError])

  if (!canWrite || slot.status === 'completed' || slot.status === 'cancelled') {
    return null
  }

  return (
    <div className="rounded-su border border-su-border bg-su-surface-muted p-3">
      <h4 className="mb-2 text-sm font-semibold text-su-text-strong">Перенос и остановка</h4>
      {inProgress && (
        <Alert kind="warn">
          Слот в работе — материалы зарезервированы. Перенос потребует пересчёта резерва.
        </Alert>
      )}

      <div className="mb-3 flex flex-wrap gap-1">
        {(
          [
            ['move', 'По времени'],
            ['transfer', 'На станок'],
            ['stop', 'Отменить'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={cn(
              btn('ghost', 'sm'),
              tab === key && 'bg-indigo-100 text-indigo-900 dark:bg-indigo-950 dark:text-indigo-100',
            )}
            onClick={() => setTab(key)}
            disabled={busy}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'move' && (
        <div className="flex flex-col gap-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <FormField label="Начало" htmlFor="op-move-start">
              <input
                id="op-move-start"
                type="datetime-local"
                className={formInputProps().className}
                value={moveStart}
                onChange={(e) => setMoveStart(e.target.value)}
                disabled={busy}
              />
            </FormField>
            <FormField label="Окончание" htmlFor="op-move-end">
              <input
                id="op-move-end"
                type="datetime-local"
                className={formInputProps().className}
                value={moveEnd}
                onChange={(e) => setMoveEnd(e.target.value)}
                disabled={busy}
              />
            </FormField>
          </div>
          <p className="m-0 text-xs text-su-text">
            Последующие слоты на этом станке сдвинутся автоматически.
          </p>
          <button type="button" className={modalBtn('primary', 'sm')} disabled={busy} onClick={() => void handleMove()}>
            {busy ? '…' : 'Перенести'}
          </button>
        </div>
      )}

      {tab === 'transfer' && (
        <div className="flex flex-col gap-2">
          <FormField label="Целевой станок" htmlFor="op-target-machine">
            <select
              id="op-target-machine"
              className={formInputProps().className}
              value={targetMachineId}
              onChange={(e) => setTargetMachineId(e.target.value)}
              disabled={busy}
            >
              {machineOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {formatMachineLabel(m)}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Начало на новом станке" htmlFor="op-transfer-start">
            <input
              id="op-transfer-start"
              type="datetime-local"
              className={formInputProps().className}
              value={transferStart}
              onChange={(e) => setTransferStart(e.target.value)}
              disabled={busy}
            />
          </FormField>
          {targetMachineId !== slot.machine && (
            <label className="flex items-center gap-2 text-sm text-su-text">
              <input
                type="checkbox"
                checked={compressSource}
                onChange={(e) => setCompressSource(e.target.checked)}
                disabled={busy}
              />
              Сжать очередь на исходном станке
            </label>
          )}
          <button
            type="button"
            className={modalBtn('primary', 'sm')}
            disabled={busy}
            onClick={() => void handleTransfer()}
          >
            {busy ? '…' : 'Перенести на станок'}
          </button>
        </div>
      )}

      {tab === 'stop' && (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-sm text-su-text">Слот получит статус «Отменён», резерв снимется.</p>
          <label className="flex items-center gap-2 text-sm text-su-text">
            <input
              type="checkbox"
              checked={compressOnCancel}
              onChange={(e) => setCompressOnCancel(e.target.checked)}
              disabled={busy}
            />
            Сдвинуть последующие слоты раньше
          </label>
          <button type="button" className={modalBtn('primary', 'sm')} disabled={busy} onClick={() => void handleCancel()}>
            {busy ? '…' : 'Отменить слот'}
          </button>
        </div>
      )}

      {msg && <p className="mt-2 mb-0 text-xs text-emerald-700">{msg}</p>}
    </div>
  )
}
